/* eslint-disable max-lines */
import { useState } from 'react';
import { FormProvider, useFieldArray, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router';
import PlusSquare from '@untitled-ui/icons-react/build/esm/PlusSquare';

import { Divider } from 'antd';
import { setDrawerCloseDisabled } from 'api/client-side-slices/DesignSlice';
import { useGetEntityRolesQuery } from 'api/EntitiesApiSlice';
import { useAddIndividualMutation, useSearchIndividualMutation } from 'api/IndividualsApiSlice';
import { useAppDispatch } from 'app-redux/hooks';
import { BaseButton, useModal } from 'components';
import { DeleteButton } from 'components/Buttons/DeleteButton';
import { useDrawer } from 'components/Drawer/Drawer';
import { ModalComponent } from 'components/Modal/Modal';
import { format } from 'date-fns';
import { useToast } from 'providers/ToastProvider';
import { EntityRole, extractStringParts, parseAddress, removeWhiteSpaces, RoleOption } from 'utils';
import { AddNewIndividualContent } from '../AddNewIndividualContent';
import { LookupIndividualContent } from '../components/LookupIndividualContent';
import { IndividualDrawerFooter } from '../IndividualDrawerFooter';
import { AddNewAssociatedPartyContent } from './AddNewAssociatedPartyContent';
import { AddNewIndividualForm } from './AddNewIndividualForm';

export const IndividualContent = () => {
  const [selectedIndividualParty, setSelectedIndividualParty] = useState<any | null>(null);

  const methods = useForm({
    mode: 'onChange',
    reValidateMode: 'onChange',
  });

  const { data: roleOptionsData } = useGetEntityRolesQuery({ type: 'Group' });
  const toast = useToast();

  const roleOptions: RoleOption[] = roleOptionsData
    ? roleOptionsData
        .map(
          (item: EntityRole): RoleOption => ({
            value: item.id || '',
            label: item.name || '',
          })
        )
        .sort((a, b) => a.label.localeCompare(b.label))
    : [];

  const creatorId = roleOptions.find((role) => role.label === 'Creator')?.value;

  const {
    control,
    handleSubmit,
    getValues,
    setValue,
    unregister,
    formState: { errors },
  } = methods;

  const onSubmit = (data: any) => {};

  const { closeDrawer } = useDrawer();
  const { openModal, closeModal } = useModal();

  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const [addIndividual, { isLoading }] = useAddIndividualMutation();
  const [searchIndividual, { data, isLoading: isSearching }] = useSearchIndividualMutation();

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'individuals',
    shouldUnregister: false,
  });

  const modalBodyText =
    'This email address is already used by another individual in your Prime KYC account. Cancel to change the email, or create a new verification using it.';

  const preventVerificationText = (
    <>
      To register for Prime KYC access, please contact{' '}
      <a href="mailto:support@primecc.com.au" className="text-blue-500">
        support@primecc.com.au
      </a>
    </>
  );

  const handleAddIndividual = () => {
    dispatch(setDrawerCloseDisabled(true));
    const existingIndividuals = getValues('individuals') || [];
    if (existingIndividuals.length === 0) {
      append({
        firstName: '',
        lastName: '',
        middleName: '',
        dateOfBirth: '',
        email: '',
        mobilePhone: '',
        relationship: '',
        address: '',
      });
    }
  };

  const handleRemoveIndividual = (index: number) => {
    remove(index);
    const fieldSuffix = `_${index + 1}00`;
    unregister([
      `firstName${fieldSuffix}`,
      `middleName${fieldSuffix}`,
      `lastName${fieldSuffix}`,
      `dateOfBirth${fieldSuffix}`,
      `email${fieldSuffix}`,
      `mobilePhone${fieldSuffix}`,
      `address${fieldSuffix}`,
    ]);
  };

  const handleStartVerification = async (forceStore: boolean = false) => {
    try {
      const { individuals, ...formValues } = getValues();
      const {
        address,
        addressInput,
        associatedAddressInput, // do not remove, this is destructed from formValues so that `existingKyCIndividual` is left with its original properties
        newAssociatedParty,
        ...existingKycIndividual
      } = formValues || {};

      const formattedAddress = addressInput || (address ? parseAddress(address) : {});

      const payload = [
        ...(Object.entries(existingKycIndividual).length
          ? [
              {
                ...existingKycIndividual,
                ...formattedAddress,
                forceStore,
                dateOfBirth: existingKycIndividual.dateOfBirth
                  ? format(new Date(existingKycIndividual.dateOfBirth), 'yyyy-MM-dd')
                  : undefined,
                roleId: creatorId,
              },
            ]
          : []),
        ...(individuals?.map((individual: any) => {
          const dateOfBirth = format(individual.dateOfBirth, 'yyyy-MM-dd');
          let streetType = individual?.streetType || '';
          let streetName = individual?.streetName || '';
          let streetNumber = individual?.streetNumber || '';

          if (!streetType && !streetNumber) {
            [streetNumber, streetName, streetType] = extractStringParts(streetName);
          }
          return {
            ...individual,
            dateOfBirth,
            mobilePhone: removeWhiteSpaces(individual.mobilePhone || ''),
            forceStore,
            streetName,
            streetNumber,
            streetType,
            roleId: creatorId,
          };
        }) ?? []),
        ...(newAssociatedParty?.map(({ address, dateOfbirth, ...individual }: any) => {
          const dateOfBirth = format(individual.dateOfBirth, 'yyyy-MM-dd');
          let streetType = address.streetType || '';
          let streetName = address.streetName || '';
          let streetNumber = address.streetNumber || '';

          if (!streetType && !streetNumber) {
            [streetNumber, streetName, streetType] = extractStringParts(address.streetName);
          }

          return {
            ...individual,
            dateOfBirth,
            mobilePhone: removeWhiteSpaces(individual.mobilePhone || ''),
            country: address.country || '',
            postcode: address.postcode || '',
            state: address.state || '',
            streetName,
            streetNumber,
            streetType,
            suburb: address.suburb || '',
          };
        }) ?? []),
      ];

      if (selectedIndividualParty) {
        payload.push({
          ...selectedIndividualParty,
          ...(selectedIndividualParty?.address
            ? parseAddress(selectedIndividualParty.address)
            : {}),
          dateOfBirth: selectedIndividualParty.dateOfBirth
            ? format(new Date(selectedIndividualParty.dateOfBirth), 'yyyy-MM-dd')
            : '',
          forceStore: true,
        });
      }

      if (!payload.length) {
        toast.error('You need to add at least 1 individual');
        return;
      }

      await addIndividual({ newIndividual: payload, isGroup: false })
        .unwrap()
        .then((response: any) => {
          navigate(`/individual-details/${response[0]?.id}`);
        });

      closeDrawer();
      closeModal();

      toast.success('Verification link created!');
    }catch (error: any) {
      let errorMessage = '';
      if (error instanceof Error) {
        errorMessage = error.message;
      } else if (typeof error === 'object' && error !== null && 'data' in error) {
        const { data } = error as { data: string | object };
        errorMessage =
          typeof data === 'string' ? data.toLowerCase().trim() : JSON.stringify(data, null, 2);
      }

      if (errorMessage.includes('person with email:')) {
        openModal(
          <ModalComponent
            bodyContent={modalBodyText}
            isLoading={isLoading}
            onCancel={closeModal}
            onUpdate={() => {
              handleStartVerification(true);
              closeModal();
            }}
          />,
          'Duplicated Email'
        );
      } else if (errorMessage.includes('to register for prime kyc access')) {
        openModal(
          <ModalComponent
            bodyContent={preventVerificationText}
            onCancel={closeModal}
            hasOneButton={true}
            buttonText="Close"
            onUpdate={() => {
              closeModal();
            }}
          />,
          'Verification failed'
        );
      } else if (errorMessage.includes('was not found in this key vault')) {
        openModal(
          <ModalComponent
            bodyContent={preventVerificationText}
            onCancel={closeModal}
            hasOneButton={true}
            buttonText="Close"
            onUpdate={() => {
              closeModal();
            }}
          />,
          'Verification failed'
        );
      } else {
        toast.error('A general error has occurred, please contact IT support for assistance.');
      }
    }
  };

  return (
    <FormProvider {...methods} handleSubmit={() => handleSubmit(onSubmit)}>
      <div className="flex flex-col justify-between overflow-auto h-[calc(100vh-150px)] mb-[20px]">
        <div className="flex justify-start items-start flex-col ml-1.5 gap-[20px]">
          <span className="font-bold text-2xl text-gray-800">Start New Verification</span>
          <AddNewIndividualContent
            disabled={!!fields.length}
            data={data ?? []}
            isLoading={isSearching}
            searchIndividual={searchIndividual}
            removeIndividual={handleRemoveIndividual}
          />

          <Divider className="m-0" />

          <LookupIndividualContent append={append} disabled={!!fields.length} />

          {!fields.length && (
            <BaseButton onClick={handleAddIndividual} className="link-button">
              <PlusSquare width={15} height={15} />
              Add New Individual
            </BaseButton>
          )}

          {fields?.map((field, index) => (
            <div key={field.id}>
              <div className="text-gray-600 font-semibold text-base flex items-center justify-between w-full">
                {index + 1}. Individual Details
                <DeleteButton onClick={() => handleRemoveIndividual(index)} />
              </div>

              <AddNewIndividualForm
                control={control}
                errors={errors}
                formIndex={index}
                hidden={true}
                setValue={setValue}
                defaultValues={field}
                manualAddress
                fieldGroup="individuals"
              />
            </div>
          ))}

          <Divider className="m-0" />

          <AddNewAssociatedPartyContent
            searchIndividual={searchIndividual}
            data={data ?? []}
            selectedIndividual={selectedIndividualParty}
            setSelectedIndividual={setSelectedIndividualParty}
            isLoading={isSearching}
          />
        </div>
      </div>
      <IndividualDrawerFooter
        handleSave={handleSubmit(() => handleStartVerification(false))}
        isLoading={isLoading}
      />
    </FormProvider>
  );
};
